<script>
  import { toastStore } from "../../stores/toast.js";

  /**
   * File Dropzone Component
   * Provides separate buttons for selecting files vs taking photos
   * Works correctly on both iOS and Android
   *
   * @prop {Array} files - Selected files array (bindable)
   * @prop {Array} accept - Accepted file types
   * @prop {number} maxSize - Max file size in bytes
   * @prop {boolean} multiple - Allow multiple files
   */
  let {
    files = $bindable([]),
    accept = [".pdf", ".doc", ".docx", ".txt", ".jpg", ".jpeg", ".png", ".gif"],
    maxSize = 10 * 1024 * 1024, // 10MB
    multiple = true,
  } = $props();

  let dragActive = $state(false);
  let fileInputRef = $state(null);
  let cameraInputRef = $state(null);

  // Separate image types for camera capture
  const imageTypes = [".jpg", ".jpeg", ".png", ".gif"];

  // Derived values that react to accept prop changes
  let documentTypes = $derived(accept.filter((t) => !imageTypes.includes(t)));
  let acceptsImages = $derived(accept.some((t) => imageTypes.includes(t)));

  function handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    dragActive = true;
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    dragActive = false;
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    dragActive = false;

    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }

  function handleFileSelect(e) {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
    e.target.value = ""; // Reset input
  }

  function addFiles(newFiles) {
    const rejectedFiles = [];
    const validFiles = newFiles.filter((file) => {
      const ext = "." + file.name.split(".").pop().toLowerCase();
      const isValidType = accept.includes(ext);
      const isValidSize = file.size <= maxSize;

      if (!isValidType) {
        rejectedFiles.push({ name: file.name, reason: "Invalid file type" });
        return false;
      }
      if (!isValidSize) {
        rejectedFiles.push({
          name: file.name,
          reason: `File size ${formatFileSize(file.size)} exceeds maximum of ${formatFileSize(maxSize)}`,
        });
        return false;
      }
      return true;
    });

    // Show error notifications for rejected files
    rejectedFiles.forEach(({ name, reason }) => {
      toastStore.error(`${name}: ${reason}`);
    });

    if (multiple) {
      files = [...files, ...validFiles];
    } else {
      files = validFiles.slice(0, 1);
    }
  }

  function removeFile(index) {
    files = files.filter((_, i) => i !== index);
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }

  function openFilePicker() {
    fileInputRef?.click();
  }

  function openCamera() {
    cameraInputRef?.click();
  }
</script>

<div class="dropzone-wrapper">
  <!-- Main dropzone for drag & drop (desktop) -->
  <div
    class="dropzone"
    class:drag-active={dragActive}
    ondragenter={handleDragEnter}
    ondragleave={handleDragLeave}
    ondragover={handleDragOver}
    ondrop={handleDrop}
    role="region"
    aria-label="File upload area"
  >
    <!-- Hidden file input - accepts all file types -->
    <input
      bind:this={fileInputRef}
      type="file"
      accept={accept.join(",")}
      {multiple}
      onchange={handleFileSelect}
      class="file-input"
      aria-hidden="true"
    />

    <!-- Hidden camera input - only for photos -->
    {#if acceptsImages}
      <input
        bind:this={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        {multiple}
        onchange={handleFileSelect}
        class="file-input"
        aria-hidden="true"
      />
    {/if}

    <div class="dropzone-content">
      <div class="dropzone-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path d="M12 16V4m0 0L7 9m5-5 5 5" />
          <path d="M20 16v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2" />
        </svg>
      </div>

      <!-- Desktop: Single click area -->
      <p class="dropzone-text desktop-only">
        Drag & drop files here or click a button below
      </p>

      <!-- Mobile: Descriptive text -->
      <p class="dropzone-text mobile-only">Select files or take a photo</p>

      <p class="dropzone-hint">
        {accept.map((t) => t.toUpperCase().replace(".", "")).join(", ")} • Max {formatFileSize(
          maxSize
        )}
      </p>

      <!-- Action buttons - always visible -->
      <div class="dropzone-actions">
        <button
          type="button"
          class="action-btn action-btn-primary"
          onclick={openFilePicker}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"
            />
            <path d="M14 2v6h6" />
            <path d="M12 18v-6" />
            <path d="m9 15 3-3 3 3" />
          </svg>
          <span>Choose Files</span>
        </button>

        {#if acceptsImages}
          <button
            type="button"
            class="action-btn action-btn-secondary"
            onclick={openCamera}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"
              />
              <circle cx="12" cy="13" r="3" />
            </svg>
            <span>Take Photo</span>
          </button>
        {/if}
      </div>
    </div>
  </div>

  {#if files.length > 0}
    <div class="file-list">
      <h4 class="file-list-title">Selected Files ({files.length})</h4>
      {#each files as file, index}
        <div class="file-item">
          <div class="file-icon">
            {#if file.type?.startsWith("image/")}
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="9" cy="9" r="2" />
                <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
              </svg>
            {:else}
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"
                />
                <path d="M14 2v6h6" />
              </svg>
            {/if}
          </div>
          <div class="file-info">
            <span class="file-name">{file.name}</span>
            <span class="file-size">{formatFileSize(file.size)}</span>
          </div>
          <button
            type="button"
            class="file-remove"
            onclick={() => removeFile(index)}
            aria-label="Remove {file.name}"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .dropzone-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .dropzone {
    border: 2px dashed var(--color-gray-200);
    border-radius: var(--radius-xl);
    padding: var(--space-6) var(--space-4);
    text-align: center;
    transition: all var(--transition-base);
    background: var(--glass-bg-light);
    backdrop-filter: blur(var(--blur-sm));
    -webkit-backdrop-filter: blur(var(--blur-sm));
  }

  .dropzone.drag-active {
    border-color: var(--color-primary-400);
    background: rgba(139, 92, 246, 0.08);
    border-style: solid;
    box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.1);
  }

  .file-input {
    display: none;
  }

  .dropzone-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-3);
  }

  .dropzone-icon {
    width: 44px;
    height: 44px;
    color: var(--color-gray-400);
    transition:
      color var(--transition-fast),
      transform var(--transition-fast);
  }

  .dropzone-icon svg {
    width: 100%;
    height: 100%;
  }

  .drag-active .dropzone-icon {
    color: var(--color-primary-500);
    transform: translateY(-4px);
  }

  .dropzone-text {
    font-size: var(--text-base);
    color: var(--color-gray-600);
    margin: 0;
  }

  .mobile-only {
    display: block;
  }

  .desktop-only {
    display: none;
  }

  @media (min-width: 640px) {
    .mobile-only {
      display: none;
    }

    .desktop-only {
      display: block;
    }

    .dropzone {
      padding: var(--space-10) var(--space-4);
    }

    .dropzone-icon {
      width: 52px;
      height: 52px;
    }
  }

  .dropzone-hint {
    font-size: var(--text-sm);
    color: var(--color-gray-400);
    margin: 0;
  }

  /* Action buttons */
  .dropzone-actions {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    width: 100%;
    max-width: 280px;
    margin-top: var(--space-3);
  }

  @media (min-width: 640px) {
    .dropzone-actions {
      flex-direction: row;
      max-width: none;
      justify-content: center;
    }
  }

  .action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-5);
    border: none;
    border-radius: var(--radius-lg);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    cursor: pointer;
    transition: all var(--transition-fast);
    min-height: var(--touch-target-min);
  }

  .action-btn svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .action-btn-primary {
    background: var(--gradient-primary);
    color: white;
    box-shadow: 0 2px 8px rgba(124, 58, 237, 0.25);
  }

  .action-btn-primary:hover {
    box-shadow: 0 4px 16px rgba(124, 58, 237, 0.35);
    transform: translateY(-1px);
  }

  .action-btn-primary:active {
    transform: scale(0.98);
    box-shadow: 0 2px 4px rgba(124, 58, 237, 0.2);
  }

  .action-btn-secondary {
    background: var(--glass-bg);
    backdrop-filter: blur(var(--blur-sm));
    color: var(--color-gray-700);
    border: 1px solid var(--color-gray-200);
  }

  .action-btn-secondary:hover {
    background: var(--glass-bg-dark);
    border-color: var(--color-gray-300);
    transform: translateY(-1px);
  }

  .action-btn-secondary:active {
    transform: scale(0.98);
  }

  /* File list */
  .file-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .file-list-title {
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-gray-600);
    margin: 0 0 var(--space-2);
  }

  .file-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3);
    background: var(--glass-bg);
    backdrop-filter: blur(var(--blur-sm));
    border-radius: var(--radius-lg);
    border: 1px solid var(--glass-border);
    transition: box-shadow var(--transition-fast);
  }

  .file-item:hover {
    box-shadow: var(--shadow-sm);
  }

  .file-icon {
    width: 32px;
    height: 32px;
    color: var(--color-primary-500);
    flex-shrink: 0;
  }

  .file-icon svg {
    width: 100%;
    height: 100%;
  }

  .file-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .file-name {
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-gray-700);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .file-size {
    font-size: var(--text-xs);
    color: var(--color-gray-400);
  }

  .file-remove {
    width: 32px;
    height: 32px;
    padding: var(--space-1);
    border: none;
    background: rgba(239, 68, 68, 0.1);
    color: var(--color-error-500);
    border-radius: var(--radius-full);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all var(--transition-fast);
  }

  .file-remove:hover {
    background: rgba(239, 68, 68, 0.15);
    transform: scale(1.05);
  }

  .file-remove svg {
    width: 16px;
    height: 16px;
  }
</style>
